import styled from 'styled-components';

import { Button } from '../../../components/atoms/button/button';
import { Spinner } from '../../../components/atoms/spinner/spinner';
import { Drawer } from '../../../components/molecules/drawer/drawer';
import { ErrorState } from '../../../components/molecules/error-state/error-state';
import {
  AudienciaAviso,
  AUDIENCIA_AVISO_LABEL,
  AvisoDetail,
} from '../../../types/aviso';
import { AvisoStatusBadge } from './aviso-status-badge';
import { AvisoTipoBadge } from './aviso-tipo-badge';
import { formatDateTime } from './format';

interface Props {
  isOpen: boolean;
  detail: AvisoDetail | null;
  status: 'idle' | 'loading' | 'error';
  errorMessage?: string | null;
  canUpdate: boolean;
  canActivate: boolean;
  canDeactivate: boolean;
  activating?: boolean;
  onClose: () => void;
  onEdit: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
}

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 80px 0;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Header = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: 14px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Badges = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border};
  }

  h3 {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.textPrimary};
    line-height: 1.5;
    white-space: pre-wrap;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;

  span:first-child {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  span:last-child {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 500;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const AvisoDetailDrawer = ({
  isOpen,
  detail,
  status,
  errorMessage,
  canUpdate,
  canActivate,
  canDeactivate,
  activating,
  onClose,
  onEdit,
  onActivate,
  onDeactivate,
}: Props) => (
  <Drawer
    isOpen={isOpen}
    onClose={onClose}
    width={480}
    title={detail?.titulo ?? 'Detalhes do aviso'}
    description={detail ? `#${detail.id.slice(0, 8)}` : undefined}
    footer={
      detail ? (
        <Footer>
          {canUpdate && (
            <Button variant="secondary" onClick={onEdit}>
              Editar
            </Button>
          )}
          {detail.ativo
            ? canDeactivate && (
                <Button
                  variant="danger"
                  onClick={onDeactivate}
                  loading={activating}
                  data-testid="aviso-action-desativar"
                >
                  Desativar
                </Button>
              )
            : canActivate && (
                <Button
                  variant="primary"
                  onClick={onActivate}
                  loading={activating}
                  data-testid="aviso-action-ativar"
                >
                  Ativar
                </Button>
              )}
        </Footer>
      ) : undefined
    }
  >
    {status === 'loading' && (
      <Loading>
        <Spinner /> Carregando aviso…
      </Loading>
    )}

    {status === 'error' && (
      <ErrorState
        title="Não foi possível carregar"
        description={errorMessage ?? 'Tente novamente mais tarde.'}
      />
    )}

    {detail && status !== 'loading' && (
      <>
        <Header>
          <Title>{detail.titulo}</Title>
          <Badges>
            <AvisoTipoBadge tipo={detail.tipo} />
            <AvisoStatusBadge status={detail.status} />
          </Badges>
        </Header>

        <Section>
          <h3>Mensagem</h3>
          <p>{detail.mensagem}</p>
        </Section>

        <Section>
          <h3>Janela de exibição</h3>
          <Row>
            <span>Início</span>
            <span>{formatDateTime(detail.dataInicio)}</span>
          </Row>
          <Row>
            <span>Expiração</span>
            <span>{formatDateTime(detail.dataExpiracao)}</span>
          </Row>
        </Section>

        <Section>
          <h3>Audiência</h3>
          <Row>
            <span>Quem vê</span>
            <span>{AUDIENCIA_AVISO_LABEL[detail.audiencia]}</span>
          </Row>
          {detail.audiencia === AudienciaAviso.ESPECIFICOS && (
            <Row>
              <span>Destinatários</span>
              <span>{detail.destinatariosCount}</span>
            </Row>
          )}
          {detail.audiencia === AudienciaAviso.ESPECIFICOS &&
            detail.destinatarios.length > 0 && (
              <ul
                style={{
                  listStyle: 'none',
                  margin: '6px 0 0',
                  padding: 0,
                  maxHeight: 180,
                  overflowY: 'auto',
                }}
                data-testid="aviso-detail-destinatarios"
              >
                {detail.destinatarios.map((d) => (
                  <li
                    key={d.id}
                    style={{
                      padding: '4px 0',
                      borderBottom: '1px dashed currentColor',
                      opacity: 0.85,
                      fontSize: 13,
                    }}
                  >
                    <strong>{d.nome || '—'}</strong>
                    {d.email ? ` · ${d.email}` : ''}
                  </li>
                ))}
              </ul>
            )}
        </Section>

        <Section>
          <h3>Metadados</h3>
          <Row>
            <span>Criado em</span>
            <span>{formatDateTime(detail.createdAt)}</span>
          </Row>
          <Row>
            <span>Atualizado em</span>
            <span>{formatDateTime(detail.updatedAt)}</span>
          </Row>
          {detail.createdById && (
            <Row>
              <span>Criado por</span>
              <span>{detail.createdById}</span>
            </Row>
          )}
          {detail.updatedById && (
            <Row>
              <span>Editado por</span>
              <span>{detail.updatedById}</span>
            </Row>
          )}
        </Section>
      </>
    )}
  </Drawer>
);
